import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export const ContactSection = ({ onSubmitContact }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "Richiesta Informazioni",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const success = await onSubmitContact(formData);
    
    if (success) {
      setFormData({ name: "", email: "", subject: "Richiesta Informazioni", message: "" });
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Sede Legale",
      details: ["Via Giannetto Masala, 6", "07100 Sassari (SS)"],
    },
    {
      icon: Phone,
      title: "Telefono",
      details: [<a href="tel:+393791187384" className="hover:text-tactical-primary transition-colors">+39 379 1187384</a>],
    },
    {
      icon: Mail,
      title: "Email",
      details: ["gdivision@outlook.it"],
    },
    {
      icon: Clock,
      title: "Orari Segreteria",
      details: ["Lun-Ven: 18:00 - 21:00", "Sab: 10:00 - 13:00"],
    },
  ];

  return (
    <section id="contatti" data-testid="contact-section" className="section-tactical py-12 bg-tactical-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-tactical-primary" />
            <span className="font-mono text-xs text-tactical-primary tracking-widest uppercase">
              Comunicazioni
            </span>
          </div>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-wider text-tactical-text uppercase">
            CONTATTA IL <span className="text-tactical-primary">COMANDO</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          <div className="bg-tactical-bg border border-tactical-border p-8 relative">
            <div className="corner-marker corner-marker-tl" />
            <div className="corner-marker corner-marker-tr" />
            <div className="corner-marker corner-marker-bl" />
            <div className="corner-marker corner-marker-br" />

            <h3 className="font-heading font-bold text-xl tracking-wider text-tactical-text uppercase mb-6">
              Invia un Messaggio
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="form-field">
                <Label className="form-label">Nome Completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Mario Rossi"
                  className="mt-2 bg-black/50 border-tactical-border focus:border-tactical-primary text-tactical-text font-body input-tactical"
                />
              </div>

              <div className="form-field">
                <Label className="form-label">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="mario@example.com"
                  className="mt-2 bg-black/50 border-tactical-border focus:border-tactical-primary text-tactical-text font-body input-tactical"
                />
              </div>

              <div className="form-field">
                <Label className="form-label">Motivo del Contatto *</Label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="mt-2 w-full bg-black/50 border border-tactical-border focus:border-tactical-primary text-tactical-text font-body p-2 outline-none h-10 transition-colors cursor-pointer"
                >
                  <option value="Richiesta Informazioni" className="bg-[#0f0f11]">Richiesta Informazioni</option>
                  <option value="Prenota una Prova" className="bg-[#0f0f11]">Prenota una Prova</option>
                  <option value="Altro" className="bg-[#0f0f11]">Altro</option>
                </select>
              </div>

              <div className="form-field">
                <Label className="form-label">Messaggio *</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  placeholder={formData.subject === "Prenota una Prova" 
                    ? "Indica la data preferita e se hai bisogno di attrezzatura (fucile/maschera)..." 
                    : "Scrivi il tuo messaggio..."}
                  className="mt-2 bg-black/50 border-tactical-border focus:border-tactical-primary text-tactical-text font-body input-tactical resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-tactical bg-tactical-primary text-black font-heading font-bold uppercase tracking-widest hover:bg-white disabled:opacity-50"
              >
                {isSubmitting ? "Invio in corso..." : (
                  <>
                    Invia Messaggio <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>

          <div className="space-y-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="flex gap-6 group">
                <div className="w-14 h-14 bg-tactical-surface border border-tactical-border flex items-center justify-center flex-shrink-0 group-hover:border-tactical-primary transition-colors">
                  <info.icon className="w-6 h-6 text-tactical-primary" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-lg tracking-wider text-tactical-text uppercase mb-2">
                    {info.title}
                  </h4>
                  {info.details.map((detail, idx) => (
                    <div key={idx} className="font-body text-tactical-text-muted">
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="relative h-64 bg-tactical-bg border border-tactical-border overflow-hidden mt-8">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-tactical-primary mx-auto mb-4" />
                  <p className="font-mono text-xs text-tactical-text-muted uppercase tracking-wider">
                    Coordinate: 40°43'09.7"N 8°33'39.9"E
                  </p>
                  <p className="font-body text-sm text-tactical-text-muted mt-2">
                    Sassari, Sardegna
                  </p>
                </div>
              </div>
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `linear-gradient(#D4FF00 1px, transparent 1px), linear-gradient(90deg, #D4FF00 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
              }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};